import { useTranslation } from "next-i18next";

import { cn } from "@/common/utils/css.utils";
import { SORT_OPTIONS, useSort } from "@/features/search/hooks/useSort";
import { Select } from "@/ui/cva/select/Select";

type SortSelectProps = {
  /** Display and width utilities — the control row hides it below `md`, where the panel takes over. */
  className?: string;
};

/** Sort in the results row. The mobile filter panel uses `SortRadioGroup` instead. */
export const SortSelect = ({ className }: SortSelectProps) => {
  const { t } = useTranslation();
  const { locked, pending, current, select } = useSort();

  if (locked) {
    return null;
  }

  return (
    /*
     * No display utility of its own — the caller supplies it, so `hidden md:flex` from the control
     * row doesn't have to out-specify a base `flex`.
     *
     * No `space-x`: the only visible child is the select, and margin-based spacing would still have
     * indented it by the gap — `> * + *` matches the select as the sr-only label's sibling whether
     * that label is displayed or not.
     */
    <label className={cn("w-full items-center", className)}>
      <span className="sr-only">{t("common:common-sort-by")}</span>
      <Select
        value={current}
        disabled={pending}
        onChange={event => select(event.target.value)}
        /*
         * Hugs its text in the control row from `md`; fills the width below it.
         *
         * Brand border and the small Button's `py-1.5 text-sm`, so it and Save search come out the
         * same height.
         */
        className="w-full border-brand-500 py-1.5 text-sm hover:border-brand-400 focus:border-brand-500 focus-within:border-brand-500 md:w-auto"
      >
        {SORT_OPTIONS.map(option => (
          <option key={option.id} value={option.id}>
            {t(option.labelKey)}
          </option>
        ))}
      </Select>
    </label>
  );
};
